import axios from 'axios';
import i18n from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// ── Request Interceptor ───────────────────────────────────────────────────────
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ── Response Interceptor ──────────────────────────────────────────────────────
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // handle 429 rate limit exceeded
        if (error.response?.status === 429) {
            const retryAfter = error.response.headers['retry-after'];
            const message = retryAfter
                ? `Rate limit exceeded. Please try again in ${Math.ceil(retryAfter / 60)} minutes.`
                : 'You have reached the daily limit for this feature. Please try again tomorrow.';
            return Promise.reject(new Error(message));
        }

        if (error.response?.status === 402) {
            const msg = error.response.data?.error || i18n.t('common.insufficient_credits');
            alert(`${msg}\n\n${i18n.t('common.go_to_credits')}.`);
            window.location.href = '/credits';
            return Promise.reject(error);
        }

        // if 401 and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem('refresh_token');
                const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
                    refresh: refreshToken,
                });

                const newAccessToken = response.data.access;
                localStorage.setItem('access_token', newAccessToken);

                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return api(originalRequest);

            } catch (refreshError) {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// ── Auth endpoints ────────────────────────────────────────────────────────────
export const authService = {
    register: (email, password, password2, language = 'en') =>
        api.post('/auth/register/', { email, password, password2, language }),

    login: async (email, password) => {
        const response = await api.post('/auth/login/', { email, password });
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);
        return response;
    },

    googleLogin: async (credential, language = 'en') => {
        const response = await api.post('/auth/google/', { credential, language });
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);
        return response;
    },

    logout: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
    },

    forgotPassword: (email) =>
        api.post('/auth/forgot-password/', { email }),

    resetPassword: (email, token, new_password) =>
        api.post('/auth/reset-password/', { email, token, new_password }),

    isLoggedIn: () => !!localStorage.getItem('access_token'),
    verifyEmail: (email, token) =>
    api.post('/auth/verify-email/', { email, token }),
    resendVerification: () =>
    api.post('/auth/resend-verification/'),
    getSettings: () => api.get('/profile/'),
    updateSettings: (data) => api.patch('/settings/', data),
};

// ── Profile endpoints ─────────────────────────────────────────────────────────
export const profileService = {
    get: () => api.get('/profile/'),
    update: (data) => api.put('/profile/', data),
};

// ── Resume endpoints ──────────────────────────────────────────────────────────
export const resumeService = {
    upload: (formData) => api.post('/resumes/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    list: () => api.get('/resumes/'),
    detail: (id) => api.get(`/resumes/${id}/`),
    delete: (id) => api.delete(`/resumes/${id}/`),
    tailor: (data) => api.post('/resumes/tailor/', data, {
        responseType: 'blob',
    }),
};

// ── Internship endpoints ──────────────────────────────────────────────────────
export const internshipService = {
    list: () => api.get('/internships/'),
    create: (data) => api.post('/internships/', data),
    update: (id, data) => api.patch(`/internships/${id}/`, data),
    delete: (id) => api.delete(`/internships/${id}/`),
    addInterview: (appId, data) => api.post(`/internships/${appId}/interviews/`, data),
    addNote: (appId, data) => api.post(`/internships/${appId}/notes/`, data),
};

export const studyService = {
    upload: (formData) => api.post('/study/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    uploadText: (title, text) => api.post('/study/upload/', { title, text }),
    getSessions: () => api.get('/study/sessions/'),
    getSession: (sessionId) => api.get(`/study/sessions/${sessionId}/`),
    askQuestion: (sessionId, question) => api.post(`/study/sessions/${sessionId}/chat/`, { question }),
    download: (sessionId) => api.get(`/study/sessions/${sessionId}/download/`, { responseType: 'blob' }),
};

// ── Career AI endpoints ───────────────────────────────────────────────────────
export const careerService = {
    getRecommendations: () => api.get('/career/recommendations/'),
    getSkillGaps: () => api.get('/career/skill-gaps/'),
    getRoadmap: (targetRole, mode = 'target') =>
        api.get(`/career/roadmap/?target_role=${encodeURIComponent(targetRole)}&mode=${mode}`),
    getInterviewPrep: (role, level = 'intern') =>
        api.get(`/career/interview-prep/?role=${encodeURIComponent(role)}&level=${level}`),
    getInterviewPrepByApp: (applicationId) =>
        api.get(`/career/interview-prep/?application_id=${applicationId}`),
    startInterview: (applicationId) =>
        api.get(`/career/interview/start/?application_id=${applicationId}`),
    evaluateAnswer: (data) => api.post('/career/interview/evaluate/', data),
    getInterviewReport: (data) => api.post('/career/interview/report/', data),
    getJobRecommendations: (jobType = 'general', country = 'worldwide') => 
    api.get(`/career/jobs/?job_type=${jobType}&country=${encodeURIComponent(country)}`),
};
// ── Analytics endpoints ───────────────────────────────────────────────────────
export const analyticsService = {
    getDashboard: () => api.get('/analytics/dashboard/'),
};

export const creditsService = {
    getPackages: () => api.get('/credits/'),
    getHistory: () => api.get('/credits/history/'),
    subscribe: (planId) => api.post('/credits/subscribe/', { plan_id: planId }),
    buyPack: (packId) => api.post('/credits/buy/', { pack_id: packId }),
    cancel: () => api.post('/credits/cancel/'),
};

export default api;